import { Suspense, type ComponentType } from 'react';

import { PageErrorBoundary } from '@/components/common/error-recovery-boundary';
import { PageChunkLoading } from '@/components/layout/page-chunk-loading';
import { isChunkLoadError, PageChunkLoadError } from '@/lib/chunk-load-recovery';

/**
 * Loads a routed page's chunk. A route's page loads only when the user
 * navigates to it, so a chunk that fails here marks a failure during
 * navigation, which the page's boundary may recover from by reloading.
 */
export async function loadPageChunk<Module>(load: () => Promise<Module>): Promise<Module> {
  try {
    return await load();
  } catch (error) {
    throw error instanceof Error && isChunkLoadError(error) ? new PageChunkLoadError(error) : error;
  }
}

/**
 * Renders a route's lazily loaded page. Its own error boundary catches a chunk
 * that failed to load (a deploy removed it) or a render error, so the shell
 * around the page keeps working and the user gets a way back.
 */
export function renderLazyPage(Page: ComponentType) {
  return (
    <PageErrorBoundary>
      <Suspense fallback={<PageChunkLoading />}>
        <Page />
      </Suspense>
    </PageErrorBoundary>
  );
}
